<template>
  <a-drawer
    :visible="visible"
    :width="480"
    :closable="true"
    :destroy-on-close="true"
    :mask-closable="false"
    wrap-class-name="itineraries-details-drawer"
    @close="onClose"
  >
    <div slot="title" class="itinerary-details__header">
      <span
        v-if="status"
        class="itinerary-details__status"
        :class="`itinerary-details__status--${statusModifier}`"
      >
        <a-icon
          :type="isActive ? 'check-circle' : 'minus-circle'"
          theme="filled"
        />
        {{ status }}
      </span>
      <h3 class="itinerary-details__title">
        <strong>{{ codigo }}</strong> {{ nome }}
      </h3>
    </div>

    <div class="itinerary-details__body">
      <div class="itinerary-details__tabs">
        <BaseSegmentedTabs
          :tabs="tabs"
          :active-key="activeTab"
          @change="onTabChange"
        />
      </div>

      <div v-show="activeTab === 'detalhes'">
      <div class="itinerary-details__section">
        <template v-if="editingName">
          <label class="itinerary-details__label">
            <span class="itinerary-details__required">*</span> Nome roteiro
          </label>
          <a-input
            ref="nameInput"
            v-model="nameDraft"
            :maxLength="120"
            placeholder="Digite o nome do roteiro"
            class="itinerary-details__name-input"
            @pressEnter="saveName"
          >
            <a
              slot="suffix"
              class="itinerary-details__save-link"
              :class="{ 'is-disabled': savingName }"
              href="#"
              @click.prevent="saveName"
            >
              {{ savingName ? "Salvando..." : "Salvar" }}
            </a>
          </a-input>
        </template>

        <template v-else>
          <div class="itinerary-details__section-head">
            <span class="itinerary-details__label">Nome</span>
            <a-tooltip v-if="canEdit" title="Editar nome">
              <button
                type="button"
                class="itinerary-details__edit-btn"
                @click="startEditName"
              >
                <a-icon type="form" />
              </button>
            </a-tooltip>
          </div>
          <div class="itinerary-details__value">
            <strong>{{ codigo }}</strong> {{ nome }}
          </div>
        </template>
      </div>

      <div class="itinerary-details__section">
        <div class="itinerary-details__section-head">
          <span class="itinerary-details__label">Paradas</span>
          <a-tooltip v-if="canEdit" title="Editar paradas">
            <button
              type="button"
              class="itinerary-details__edit-btn"
              @click="$emit('edit-stops', local)"
            >
              <a-icon type="form" />
            </button>
          </a-tooltip>
        </div>
        <div class="itinerary-details__value">
          {{ stops.length }} {{ stops.length === 1 ? "parada" : "paradas" }}
        </div>
      </div>

      <h4 class="itinerary-details__list-title">Paradas</h4>
      <div class="itinerary-details__list">
        <div
          v-for="parada in stops"
          :key="parada.id"
          class="itinerary-details__card"
        >
          <span class="itinerary-details__card-icon">
            <a-icon type="environment" />
          </span>
          <div class="itinerary-details__card-text">
            <span class="itinerary-details__card-name">{{ parada.nome }}</span>
            <span class="itinerary-details__card-subtitle">
              <span class="itinerary-details__subtitle-text">
                {{ formatCnpj(parada.cnpj) }}
              </span>
              <span class="itinerary-details__dot" />
              <span class="itinerary-details__subtitle-text">
                {{ parada.cidade }} - {{ parada.uf }}
              </span>
            </span>
          </div>
        </div>
      </div>
      </div>

      <div v-show="activeTab === 'historico'" class="itinerary-history">
        <div class="itinerary-history__filters">
          <BaseHistoryFilters
            :key="historyFiltersKey"
            :users="availableUsers"
            :event-options="eventOptions"
            @change="onHistoryFilterChange"
          />
        </div>

        <div v-if="historyLoading && !history.length" class="itinerary-history__loading">
          <a-spin />
        </div>

        <a-empty
          v-else-if="historyLoaded && !history.length"
          :description="historyEmptyText"
          class="itinerary-history__empty"
        />

        <template v-else>
          <div
            v-for="group in historyGroups"
            :key="group.key"
            class="itinerary-history__group"
          >
            <div class="itinerary-history__date">
              <span class="itinerary-history__day">{{ group.dayLabel }}</span>
              <span class="itinerary-history__year">{{ group.yearLabel }}</span>
            </div>

            <div
              v-for="entry in group.items"
              :key="entry.id"
              class="itinerary-history__item"
            >
              <div class="itinerary-history__item-head">
                <span class="itinerary-history__avatar">{{ entry.initials }}</span>
                <span class="itinerary-history__name">{{ entry.userName }}</span>
                <span class="itinerary-history__time">{{ entry.timestamp }}</span>
              </div>
              <p class="itinerary-history__bubble">{{ entry.description }}</p>
            </div>
          </div>

          <a-button
            v-if="hasMoreHistory"
            class="itinerary-history__more"
            block
            :loading="historyLoading"
            @click="loadMoreHistory"
          >
            Carregar mais
          </a-button>
        </template>
      </div>
    </div>

    <div class="itinerary-details__footer">
      <a-button
        size="large"
        class="itinerary-details__btn"
        @click="onClose"
      >
        Fechar
      </a-button>
      <a-button
        v-if="canChangeStatus"
        type="primary"
        size="large"
        class="itinerary-details__btn itinerary-details__btn--primary"
        :loading="statusPending"
        :disabled="editingName"
        @click="toggleStatus"
      >
        {{ isActive ? "Inativar roteiro" : "Ativar roteiro" }}
      </a-button>
    </div>
  </a-drawer>
</template>

<script>
import { handleApiError } from "@/modules/Itineraries/utils/handleApiError";
import { ITINERARY_STATUS } from "@/modules/Itineraries/constants/itineraryStatus";
import BaseSegmentedTabs from "@/shared/components/BaseSegmentedTabs.vue";
import BaseHistoryFilters from "@/shared/components/BaseHistoryFilters.vue";
import { ITINERARY_HISTORY_EVENTS } from "../../constants/itineraryHistoryEvents";

export default {
  name: "ItinerariesDetailsDrawer",
  components: { BaseSegmentedTabs, BaseHistoryFilters },
  props: {
    visible: { type: Boolean, default: false },
    itinerary: { type: Object, default: null },
    canUpdate: { type: Boolean, default: false },
    canChangeStatus: { type: Boolean, default: false },
  },
  data() {
    return {
      local: null,
      editingName: false,
      nameDraft: "",
      savingName: false,
      statusPending: false,
      activeTab: "detalhes",
      history: [],
      historyMeta: null,
      historyPage: 1,
      historyLoading: false,
      historyLoaded: false,
      historyFilters: null,
      availableUsers: [],
      historyKey: 0,
      eventOptions: ITINERARY_HISTORY_EVENTS,
    };
  },
  computed: {
    tabs() {
      return [
        { key: "detalhes", label: "Detalhes" },
        { key: "historico", label: "Histórico" },
      ];
    },
    historyGroups() {
      const groups = [];
      const byKey = {};
      this.history.forEach((entry) => {
        if (!byKey[entry.dateKey]) {
          byKey[entry.dateKey] = {
            key: entry.dateKey,
            dayLabel: entry.dayLabel,
            yearLabel: entry.yearLabel,
            items: [],
          };
          groups.push(byKey[entry.dateKey]);
        }
        byKey[entry.dateKey].items.push(entry);
      });
      return groups;
    },
    hasMoreHistory() {
      return (
        !!this.historyMeta &&
        this.historyMeta.current_page < this.historyMeta.last_page
      );
    },
    historyFiltersKey() {
      return this.historyKey;
    },
    hasActiveHistoryFilter() {
      const f = this.historyFilters;
      if (!f) return false;
      return !!(
        f.search ||
        (f.events && f.events.length) ||
        f.userId ||
        f.dateFrom ||
        f.dateTo
      );
    },
    historyEmptyText() {
      return this.hasActiveHistoryFilter
        ? "Nenhum resultado para os filtros aplicados."
        : "Nenhuma alteração registrada.";
    },
    codigo() {
      return (this.local && this.local.id) || "";
    },
    nome() {
      return (this.local && this.local.nome) || "";
    },
    stops() {
      return (this.local && this.local.stops) || [];
    },
    status() {
      return (this.local && this.local.status) || "";
    },
    isActive() {
      return !!this.local && this.local.statusValor === ITINERARY_STATUS.ATIVO;
    },
    statusModifier() {
      return this.isActive ? "ativo" : "inativo";
    },
    canEdit() {
      return this.isActive && this.canUpdate;
    },
  },
  watch: {
    itinerary: {
      immediate: true,
      handler(value) {
        this.local = value ? { ...value } : null;
        this.activeTab = "detalhes";
        this.historyKey += 1;
        this.resetHistory();
      },
    },
    visible(open) {
      if (!open) {
        this.editingName = false;
        this.savingName = false;
        this.statusPending = false;
      }
    },
  },
  methods: {
    onClose() {
      this.$emit("close");
    },
    onTabChange(key) {
      this.activeTab = key;
      if (key === "historico" && !this.historyLoaded && !this.historyLoading) {
        this.loadHistory(1);
      }
    },
    resetHistory() {
      this.history = [];
      this.historyMeta = null;
      this.historyPage = 1;
      this.historyLoading = false;
      this.historyLoaded = false;
      this.historyFilters = null;
      this.availableUsers = [];
    },
    onHistoryFilterChange(filters) {
      this.historyFilters = filters;
      this.loadHistory(1);
    },
    async loadHistory(page) {
      if (this.historyLoading || !this.local) return;
      this.historyLoading = true;
      try {
        const { entries, meta } = await this.$store.dispatch(
          "itineraries/fetchItineraryHistory",
          { itineraryId: this.local.itineraryId, page, filters: this.historyFilters }
        );
        this.history = page > 1 ? this.history.concat(entries) : entries;
        this.historyMeta = meta;
        this.historyPage = meta ? meta.current_page : page;
        this.availableUsers = meta && meta.availableUsers ? meta.availableUsers : [];
        this.historyLoaded = true;
      } catch (error) {
        handleApiError(this, error, "Não foi possível carregar o histórico");
      } finally {
        this.historyLoading = false;
      }
    },
    loadMoreHistory() {
      this.loadHistory(this.historyPage + 1);
    },
    startEditName() {
      this.nameDraft = this.nome;
      this.editingName = true;
      this.$nextTick(() => {
        if (this.$refs.nameInput) this.$refs.nameInput.focus();
      });
    },
    async saveName() {
      const descr = (this.nameDraft || "").trim();
      if (!descr || this.savingName) return;
      this.savingName = true;
      try {
        await this.$store.dispatch("itineraries/updateItineraryDetails", {
          itineraryId: this.local.itineraryId,
          descr,
          obs: this.local.descricao || "",
        });
        this.local = { ...this.local, nome: descr };
        this.editingName = false;
        this.$emit("changed");
      } catch (error) {
        handleApiError(this, error, "Não foi possível salvar o nome do roteiro");
      } finally {
        this.savingName = false;
      }
    },
    async toggleStatus() {
      if (this.editingName || this.statusPending) return;
      this.statusPending = true;
      const newStatus = this.isActive
        ? ITINERARY_STATUS.INATIVO
        : ITINERARY_STATUS.ATIVO;
      try {
        await this.$store.dispatch("itineraries/updateItinerariesStatus", {
          itineraryIds: [this.local.itineraryId],
          status: newStatus,
        });
        this.local = {
          ...this.local,
          statusValor: newStatus,
          status: newStatus === ITINERARY_STATUS.ATIVO ? "Ativo" : "Inativo",
        };
      } catch (error) {
        handleApiError(this, error, "Não foi possível alterar o status do roteiro");
      } finally {
        this.statusPending = false;
      }
    },
    formatCnpj(cnpj) {
      const digits = String(cnpj || "").replace(/\D/g, "");
      if (digits.length !== 14) return cnpj || "";
      return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.itinerary-details {
  &__header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;

    &--ativo {
      color: @color-success;
    }

    &--inativo {
      color: @color-text-muted;
    }
  }

  &__title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: @color-text-dark;

    strong {
      font-weight: 700;
    }
  }

  &__body {
    padding-bottom: 80px;
  }

  &__tabs {
    margin-bottom: 20px;
  }

  &__section {
    margin-bottom: 20px;
  }

  &__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__label {
    font-size: 14px;
    font-weight: 600;
    color: @color-text-dark;
  }

  &__required {
    color: @color-danger;
  }

  &__value {
    margin-top: 4px;
    font-size: 14px;
    color: @color-text-medium;

    strong {
      color: @color-text-dark;
      font-weight: 600;
    }
  }

  &__name-input {
    margin-top: 8px;

    /deep/ .ant-input {
      border-radius: 6px;
    }
  }

  &__save-link {
    font-weight: 500;
    color: @color-primary;

    &:hover {
      color: @color-primary-hover;
    }

    &.is-disabled {
      pointer-events: none;
      opacity: 0.55;
    }
  }

  &__edit-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background: transparent;
    border-radius: 4px;
    color: @color-primary;
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }
  }

  &__list-title {
    font-size: 13px;
    font-weight: 600;
    color: @color-text-dark;
    margin: 24px 0 12px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__card {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 8px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 8px;
  }

  &__card-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 20px;
    height: 20px;
    border-radius: 99px;
    background: rgba(0, 0, 0, 0.04);
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  &__card-text {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__card-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 600;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.88);
  }

  &__card-subtitle {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }

  &__subtitle-text {
    font-size: 12px;
    line-height: 20px;
    color: rgba(0, 0, 0, 0.45);
  }

  &__dot {
    flex: none;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.15);
  }

  &__footer {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 100%;
    border-top: 1px solid #f0f0f0;
    padding: 16px 24px;
    background: #fff;
    display: flex;
    gap: 12px;
  }

  &__btn {
    flex: 1;
    border-radius: 6px;

    &--primary {
      background-color: @color-primary;
      border-color: @color-primary;

      &:hover,
      &:focus {
        background-color: @color-primary-hover;
        border-color: @color-primary-hover;
      }
    }
  }
}

.itinerary-history {
  &__filters {
    position: sticky;
    top: 0;
    z-index: 2;
    padding-top: 4px;
    background: @color-bg-surface;
  }

  &__loading,
  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
  }

  &__group {
    margin-bottom: 20px;
  }

  &__date {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid @color-border-light;
  }

  &__day {
    font-size: 15px;
    font-weight: 600;
    color: @color-text-dark;
  }

  &__year {
    font-size: 13px;
    color: @color-text-light;
  }

  &__item {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: @color-bg-subtle;
    font-size: 11px;
    font-weight: 600;
    color: @color-text-medium;
  }

  &__name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    color: @color-text-dark;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__time {
    flex: none;
    font-size: 12px;
    color: @color-text-light;
  }

  &__bubble {
    margin: 0 0 0 36px;
    padding: 10px 12px;
    background: @color-bg-subtle;
    border-radius: 8px;
    font-size: 13px;
    line-height: 20px;
    color: @color-text-medium;
  }

  &__more {
    width: 100%;
    border-radius: 6px;
  }
}
</style>

<style lang="less">
.itineraries-details-drawer {
  .ant-drawer-body {
    padding-bottom: 88px;
  }

  .ant-drawer-title {
    width: 100%;
  }
}
</style>
